/**
 * The organisation this browser tab is on (#6773).
 *
 * The server used to scope a request by the person's saved selection, one value shared by every tab, so with two
 * tabs on two clients the older tab read and wrote the newer tab's client under its own client's name. Each request
 * now names its organisation in `X-Organization-Id` (set in `fetchWithAuth`), and the tab remembers which one it is
 * on in `sessionStorage`, which is per tab and survives a reload: a reloaded tab comes back on its own organisation
 * instead of adopting whichever one another tab picked last. The saved selection only decides what a new tab opens
 * on.
 *
 * `organizationDataAtom` in `store.ts` stays the state; the tab's memory is written by its setter alone and read
 * here only to seed a tab that is loading.
 */

/** The header every request names its organisation in; the server checks it like a switch to that organisation. */
export const ORGANIZATION_HEADER = 'X-Organization-Id';

/** The server's mark on a refusal of the tab's own organisation (removed from it, or it was archived). */
export const ORGANIZATION_REFUSED_HEADER = 'X-Organization-Refused';

const TAB_ORGANIZATION_KEY = 'glitch:tab-organization-id';
const RESET_AT_KEY = 'glitch:tab-organization-reset-at';

/** A refused tab reloads at most once in this window, so a reload that does not help cannot loop. */
export const TAB_ORGANIZATION_RESET_COOLDOWN_MS = 60_000;

/** The organisation this tab was on before it (re)loaded, or undefined for a new tab. */
export function readTabOrganizationId(): number | undefined {
  const stored = sessionStorage.getItem(TAB_ORGANIZATION_KEY);
  return stored === null ? undefined : Number(stored);
}

/** Remember the organisation this tab is on, or forget it. Called by `organizationDataAtom`'s setter. */
export function rememberTabOrganization(organizationId: number | undefined): void {
  if (organizationId === undefined) {
    sessionStorage.removeItem(TAB_ORGANIZATION_KEY);
  } else {
    sessionStorage.setItem(TAB_ORGANIZATION_KEY, String(organizationId));
  }
}

/**
 * The organisation a loading tab opens on: the one it was already on, else the person's saved selection, as long as
 * the person is still an active member of it. Undefined when neither applies; the caller picks a default.
 */
export function chooseTabOrganization<T extends { id: number; membership?: { status?: string } }>(
  organizations: T[],
  savedOrganizationId: number | null | undefined,
): T | undefined {
  const active = organizations.filter(organization => organization.membership?.status === 'active');
  const find = (id: number | null | undefined) =>
    id === null || id === undefined ? undefined : active.find(organization => organization.id === id);
  return find(readTabOrganizationId()) ?? find(savedOrganizationId);
}

/**
 * The server refused the organisation this tab is on: forget it and reload, so the tab opens on one the person can
 * act in. At most once per cooldown; within it the refusal reaches the page as an ordinary error.
 */
export function resetRefusedTabOrganization(): void {
  const lastResetAt = Number(sessionStorage.getItem(RESET_AT_KEY) ?? 0);
  if (Date.now() - lastResetAt < TAB_ORGANIZATION_RESET_COOLDOWN_MS) {
    return;
  }
  sessionStorage.setItem(RESET_AT_KEY, String(Date.now()));
  rememberTabOrganization(undefined);
  window.location.reload();
}

/**
 * Hand the tab to a new identity (#7070): forget the organisation it was on and load `target` as a new page.
 *
 * Every change of identity (signing in or up, the session running out, starting or stopping impersonation) goes
 * through here. A new page starts on a new atom store and query cache, so the next identity never opens on the
 * previous one's organisation, campaigns or cached profile, whichever way the tab got there. Without forgetting the
 * tab's organisation it would still open on it whenever it also belonged to it, since the tab keeps it in
 * sessionStorage across a load.
 */
export function openAsNewIdentity(target: string): void {
  rememberTabOrganization(undefined);
  window.location.assign(target);
}
